import { useEffect, useReducer, useState } from 'react';
import { IntakeTab } from './tabs/IntakeTab';
import { AgentsTab } from './tabs/AgentsTab';
import { ChatTab } from './tabs/ChatTab';
import { Setup } from './components/Setup';
import { TabBar } from './components/TabBar';
import { ConnectionDot } from './components/ConnectionDot';
import { isConfigured } from './config';
import { openInboxStream } from './sse';
import { ensurePushSubscribed } from './push';
import { getAgents, getOpenQuestions } from './api';
import type { AgentState, PermissionRequest, Question, Tab } from './types';

interface State {
  agents: Record<string, AgentState>;
  questions: Record<string, Question>;
  permissions: Record<string, PermissionRequest>;
  connected: boolean;
}

type Action =
  | { type: 'set_connected'; connected: boolean }
  | { type: 'upsert_agent'; agent: AgentState }
  | { type: 'set_agents'; agents: AgentState[] }
  | { type: 'upsert_question'; question: Question }
  | { type: 'set_questions'; questions: Question[] }
  | { type: 'answer_question'; id: string; answer: Question['answer']; status: 'answered' | 'defaulted' }
  | { type: 'upsert_permission'; permission: PermissionRequest }
  | { type: 'remove_permission'; id: string };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'set_connected':
      return { ...state, connected: action.connected };
    case 'upsert_agent':
      return { ...state, agents: { ...state.agents, [action.agent.agent_id]: action.agent } };
    case 'set_agents': {
      const next: Record<string, AgentState> = {};
      for (const a of action.agents) next[a.agent_id] = a;
      return { ...state, agents: next };
    }
    case 'upsert_question':
      return { ...state, questions: { ...state.questions, [action.question.id]: action.question } };
    case 'set_questions': {
      const next: Record<string, Question> = {};
      for (const q of action.questions) next[q.id] = q;
      return { ...state, questions: next };
    }
    case 'answer_question': {
      const existing = state.questions[action.id];
      if (!existing) return state;
      return {
        ...state,
        questions: {
          ...state.questions,
          [action.id]: { ...existing, status: action.status, answer: action.answer },
        },
      };
    }
    case 'upsert_permission':
      return {
        ...state,
        permissions: { ...state.permissions, [action.permission.id]: action.permission },
      };
    case 'remove_permission': {
      const { [action.id]: _drop, ...rest } = state.permissions;
      return { ...state, permissions: rest };
    }
    default:
      return state;
  }
}

const INITIAL: State = {
  agents: {},
  questions: {},
  permissions: {},
  connected: false,
};

export function App() {
  const [configured, setConfigured] = useState(isConfigured());
  const [tab, setTab] = useState<Tab>('chat');
  const [state, dispatch] = useReducer(reducer, INITIAL);

  useEffect(() => {
    if (!configured) return;
    let cancelled = false;
    (async () => {
      try {
        const [{ agents }, { questions }] = await Promise.all([getAgents(), getOpenQuestions()]);
        if (cancelled) return;
        dispatch({ type: 'set_agents', agents });
        dispatch({ type: 'set_questions', questions });
      } catch (e) {
        console.warn('cold load failed', e);
      }
    })();

    const close = openInboxStream((ev) => {
      switch (ev.type) {
        case 'connected':
          dispatch({ type: 'set_connected', connected: true });
          break;
        case 'disconnected':
          dispatch({ type: 'set_connected', connected: false });
          break;
        case 'agent_state':
          dispatch({ type: 'upsert_agent', agent: ev.data.state });
          break;
        case 'question_new':
          dispatch({ type: 'upsert_question', question: ev.data.question });
          break;
        case 'question_answered':
          dispatch({
            type: 'answer_question',
            id: ev.data.question_id,
            answer: ev.data.answer,
            status: 'answered',
          });
          break;
        case 'question_defaulted':
          dispatch({
            type: 'answer_question',
            id: ev.data.question_id,
            answer: undefined,
            status: 'defaulted',
          });
          break;
        case 'permission_new':
          dispatch({ type: 'upsert_permission', permission: ev.data.request });
          break;
      }
    });

    ensurePushSubscribed().catch(() => {});

    const onSwMessage = (event: MessageEvent) => {
      if (event.data?.type === 'open-inbox') setTab('chat');
    };
    navigator.serviceWorker?.addEventListener('message', onSwMessage);

    return () => {
      cancelled = true;
      close();
      navigator.serviceWorker?.removeEventListener('message', onSwMessage);
    };
  }, [configured]);

  if (!configured) {
    return <Setup onDone={() => setConfigured(true)} />;
  }

  const inboxCount =
    Object.values(state.questions).filter((q) => q.status === 'open').length +
    Object.values(state.permissions).filter((p) => p.status === 'open').length;

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <span className="brand-mark">P</span>
          <span className="brand-name">Pocket</span>
        </div>
        <ConnectionDot connected={state.connected} />
      </header>

      <main className="app-main">
        {tab === 'intake' && <IntakeTab />}
        {tab === 'agents' && <AgentsTab agents={Object.values(state.agents)} />}
        {tab === 'chat' && (
          <ChatTab
            agents={Object.values(state.agents)}
            questions={Object.values(state.questions)}
            permissions={Object.values(state.permissions)}
            onPermissionDecided={(id) => dispatch({ type: 'remove_permission', id })}
          />
        )}
      </main>

      <TabBar current={tab} onChange={setTab} chatBadge={inboxCount} />
    </div>
  );
}
